'use client';

/**
 * GlowBackground — the signature v4 backdrop.
 * ----------------------------------------------------------------------------
 * Renders soft, heavily-blurred violet + emerald radial blobs behind content.
 * Absolutely positioned, pointer-events:none, low z-index. Drop it as the first
 * child of a `.v4-section` (position: relative) wrapper:
 *
 *   <section className="v4-section" style={{ minHeight: "100vh" }}>
 *     <GlowBackground />
 *     <div className="v4-container">…</div>
 *   </section>
 *
 * Tune with `intensity` (opacity multiplier) or pass custom `blobs`.
 */

import type { CSSProperties } from 'react';

export interface GlowBlob {
  /** Any CSS color, e.g. var(--v4-glow-violet) or an rgba(). */
  color: string;
  /** Diameter (any CSS length). Defaults to 40rem. */
  size?: string;
  /** Positioning — mirrors CSS box offsets. */
  top?: string;
  left?: string;
  right?: string;
  bottom?: string;
  /** Base opacity before `intensity` is applied. */
  opacity?: number;
  /** Blur radius. Defaults to 110px (the mockup value). */
  blur?: string;
}

export interface GlowBackgroundProps {
  /** Global opacity multiplier applied to every blob. 0..1. Default 1. */
  intensity?: number;
  /** Override the default violet+emerald pair with a custom blob set. */
  blobs?: GlowBlob[];
  /** Extra styles for the absolutely-positioned wrapper. */
  style?: CSSProperties;
  className?: string;
}

/** Default: violet top-left, emerald bottom-right — the mockup arrangement. */
const DEFAULT_BLOBS: GlowBlob[] = [
  {
    color: 'var(--v4-glow-violet)',
    size: '40rem',
    top: '-10%',
    left: '-10%',
    opacity: 0.55,
  },
  {
    color: 'var(--v4-glow-emerald)',
    size: '36rem',
    bottom: '-15%',
    right: '-10%',
    opacity: 0.5,
  },
];

export function GlowBackground({
  intensity = 1,
  blobs = DEFAULT_BLOBS,
  style,
  className,
}: GlowBackgroundProps) {
  return (
    <div
      aria-hidden
      className={className}
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 0,
        ...style,
      }}
    >
      {blobs.map((b, i) => {
        const size = b.size ?? '40rem';
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              height: size,
              width: size,
              top: b.top,
              left: b.left,
              right: b.right,
              bottom: b.bottom,
              borderRadius: '9999px',
              background: b.color,
              filter: `blur(${b.blur ?? '110px'})`,
              opacity: (b.opacity ?? 0.5) * intensity,
              pointerEvents: 'none',
            }}
          />
        );
      })}
    </div>
  );
}

export default GlowBackground;
